import type { CSSProperties } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { cn } from '@/utils/cn';
import {
  matchesWorkspaceDatePreset,
  type DateRange,
  type WorkspaceDatePresetId,
} from '@/api/reportDateRanges';
import {
  formatPctChangeLabel,
  formatSnapshotCurrency,
  percentChangeVsBaseline,
} from '@/api/snapshotMetrics';

const TITLE = 'Total Spend';

/**
 * Existing Previous/baseline series color on the spend hero.
 * Shared by the Previous bar and Previous legend circle.
 */
const PREVIOUS_COMPARISON_COLOR = 'rgba(255, 255, 255, 0.35)';

/** Plain-language name for the comparison baseline, by active date preset. */
const COMPARE_PERIOD_PHRASES: Record<WorkspaceDatePresetId, string> = {
  today: 'yesterday',
  yesterday: 'prior day',
  last7: 'prior 7 days',
  last14: 'prior 14 days',
  last30: 'prior 30 days',
  last60: 'prior 60 days',
  lastWeek: 'prior week',
  thisMonth: 'last month',
  lastMonth: 'prior month',
};

function resolveComparePeriodPhrase(current: DateRange, previous: DateRange): string {
  for (const id of Object.keys(COMPARE_PERIOD_PHRASES) as WorkspaceDatePresetId[]) {
    if (matchesWorkspaceDatePreset(id, current, previous)) {
      return COMPARE_PERIOD_PHRASES[id];
    }
  }
  return 'previous period';
}

function comparisonContextLabel(
  current: number,
  previous: number,
  periodPhrase: string
): string {
  const period = periodPhrase.toUpperCase();
  if (current < previous) return `LOWER THAN ${period}`;
  if (current > previous) return `HIGHER THAN ${period}`;
  return `SAME AS ${period}`;
}

function safeNonNeg(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/**
 * Relative magnitude widths vs the larger of current/previous.
 * Larger value = 100%; smaller is proportional. Not a % change chart.
 */
function relativeBarWidths(
  current: number,
  previous: number
): { currentPct: number; previousPct: number } {
  const c = safeNonNeg(current);
  const p = safeNonNeg(previous);
  const maxValue = Math.max(c, p);
  if (maxValue <= 0) {
    return { currentPct: 100, previousPct: 100 };
  }
  return {
    currentPct: (c / maxValue) * 100,
    previousPct: (p / maxValue) * 100,
  };
}

/** Change badge: green when up, red when down, grey when equal — by numeric comparison. */
function SpendChangeBadge({
  current,
  previous,
}: {
  current: number;
  previous: number;
}) {
  const pct = percentChangeVsBaseline(current, previous);

  if (pct.type === 'baseline_zero') {
    return <span className="text-xs font-medium text-white/70">—</span>;
  }

  const label = formatPctChangeLabel(pct);
  const c = safeNonNeg(current);
  const p = safeNonNeg(previous);

  if (pct.type === 'both_zero' || c === p) {
    return (
      <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-white">
        {label}
      </span>
    );
  }

  const isUp = c > p;
  const TrendIcon = isUp ? TrendingUp : TrendingDown;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none',
        isUp ? 'bg-[#86EFAC] text-[#166534]' : 'bg-[#FCA5A5] text-[#991B1B]'
      )}
    >
      <TrendIcon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </span>
  );
}

/**
 * Two stacked magnitude bars: widths relative to max(current, previous).
 * Current = white; Previous = shared --previous-comparison-color.
 */
function MagnitudeCompareBars({
  current,
  previous,
}: {
  current: number;
  previous: number;
}) {
  const { currentPct, previousPct } = relativeBarWidths(current, previous);

  return (
    <div
      className="flex min-w-0 flex-col gap-2.5"
      style={
        {
          '--previous-comparison-color': PREVIOUS_COMPARISON_COLOR,
        } as CSSProperties
      }
      role="img"
      aria-label={`Current spend ${formatSnapshotCurrency(current)}, previous spend ${formatSnapshotCurrency(previous)}`}
    >
      <div className="min-w-0">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" aria-hidden />
          Current
        </div>
        <div className="h-2.5 w-full min-w-0">
          <div
            className="h-full max-w-full rounded-full bg-white"
            style={{ width: `${currentPct}%` }}
          />
        </div>
      </div>

      <div className="min-w-0">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: 'var(--previous-comparison-color)' }}
            aria-hidden
          />
          Previous
        </div>
        <div className="h-2.5 w-full min-w-0">
          <div
            className="h-full max-w-full rounded-full"
            style={{
              width: `${previousPct}%`,
              background: 'var(--previous-comparison-color)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function SnapshotSpendHero({
  current,
  previous,
  compareEnabled,
}: {
  current: number;
  previous: number;
  compareEnabled: boolean;
}) {
  const { range } = useAppDateRange();
  const periodPhrase = resolveComparePeriodPhrase(range.current, range.previous);
  const contextLabel = comparisonContextLabel(current, previous, periodPhrase);

  return (
    <Card className="flex h-fit w-full flex-col self-start border-transparent bg-gradient-to-br from-violet-500 via-violet-600 to-violet-700 p-5 text-white shadow-lg shadow-violet-900/20">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white">
          {TITLE}
        </p>

        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <p className="text-[2rem] font-bold leading-none tracking-tight text-white sm:text-[2.25rem]">
            {formatSnapshotCurrency(current)}
          </p>
          {compareEnabled ? (
            <>
              <SpendChangeBadge current={current} previous={previous} />
              <span className="text-xs font-semibold italic uppercase tracking-wide text-white">
                {contextLabel}
              </span>
            </>
          ) : null}
        </div>

        {compareEnabled ? (
          <p className="mt-1.5 text-sm text-white/70">
            Previous period: {formatSnapshotCurrency(previous)}
          </p>
        ) : null}
      </div>

      {compareEnabled ? (
        <div className="mt-3 min-w-0">
          <MagnitudeCompareBars current={current} previous={previous} />
        </div>
      ) : null}
    </Card>
  );
}
